<template>
    <base-dialog :loading="loading" :value="dialog" :width="480" :title="$t('common.add_block_to_routine')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="dlg-fields">
            <div class="ab-type">
                <span class="ab-type-label">{{ $t('exercises.type') }}</span>
                <kimsa-segmented :items="typeOptions" :value="type" @change="onChangeType" />
            </div>
            <kimsa-combobox :label="$t('exercises.tags')" :placeholder="$t('common.search')" dense searchable
                multiple-with-chips placeholder-class item-text="label" :items="tagOptions" :value="tagsValue"
                @change="tags = toTagNames($event)" ref="tags" />
            <kimsa-combobox :label="$t('common.folder')" dense :items="folderOptions" item-text="label"
                @change="folder = $event" ref="folders" />
            <div class="ab-block-row">
                <kimsa-combobox class="flex-grow-1" :label="$t('common.name')" dense :items="blockOptions"
                    @change="block = $event" ref="blocks" can-write no-create-exercise :loading="loadingSearch"
                    @search="search = $event" :readonly="!folder" item-value="objectID" />
                <v-menu open-on-hover :disabled="!block" offset-y :nudge-bottom="6" max-width="300"
                    content-class="ab-preview-menu">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn class="ab-preview-btn" icon color="secondary" :disabled="!block" v-bind="attrs" v-on="on">
                            <v-icon>mdi-eye</v-icon>
                        </v-btn>
                    </template>
                    <div class="ab-preview-list">
                        <exercise-event v-for="item in blockExercises" :key="item.uid" :event="item" is-hover-view />
                    </div>
                </v-menu>
            </div>
            <div v-if="hiddenBlocks" class="ab-hint">{{ $t('common.empty_blocks_hidden') }}</div>
            <kimsa-combobox :label="$t('common.block_position')" dense :items="positionOptions" :value="position"
                @change="position = $event" ref="positions" />
        </div>

        <template v-slot:footer>
            <dialog-actions hide-cancel :confirm-label="$t('common.save')" :disabled="disabled" :loading="loading"
                @confirm="save" />
        </template>
    </base-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db, FieldValue } from "@/utils/Firebase";
import { BlockIndex, RoutineIndex } from "@/utils/Algolia";
import { debounce } from '@/utils/Common'
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaSegmented from "@/components/common/inputs/KimsaSegmented";
import { updateAssignedRoutinesGroup, getPayloadBaseAssignedRoutine, deleteAssignedRoutinesGroup, addAssignedRoutinesGroup, clearBlock } from "@/utils/Exercises"
import ExerciseEvent from '../../calendar/ExerciseEvent.vue';
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";

export default {
    name: "AddBlockDialog",
    components: { KimsaCombobox, KimsaSegmented, ExerciseEvent, BaseDialog, DialogActions },
    props: {
        currentSelection: {
            type: Object,
            default: null,
        },
        currentRoutines: {
            type: Array,
            default: null,
        },
        collection: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null
        },
        assignedRoutineToReplace: {
            type: Array,
            default: () => [],
        },
        routineNumber: {
            type: Number,
            default: 1,
        },
        noSave: {
            type: Boolean,
            default: false,
        },
        blocksQuantity: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            type: 'block',
            tags: [],
            assignedRoutine: null,
            folder: null,
            blockOptions: [],
            hiddenBlocks: 0,
            block: null,
            position: null,
            search: '',
            loadingSearch: false,
            date: null,
            blockColor: '#66CCCC',
        }
    },
    computed: {
        ...mapState(['folders', 'globalFolders', 'categoriesOptions']),
        isRoutineType(vm = this) {
            return vm.type === 'routine'
        },
        typeOptions(vm = this) {
            return [
                { name: vm.$tc('common.blocks', 1), value: 'block' },
                { name: vm.$t('common.routine'), value: 'routine' },
            ]
        },
        tagOptions(vm = this) {
            const categories = vm.categoriesOptions.map(category => ({
                name: category.name,
                value: category.name,
                label: vm.categoryLabel(category.name),
            }))
            categories.push({ name: 'circuit', value: 'circuit', label: vm.$t('circuits.circuit') })
            return categories.sort((a, b) => a.label.localeCompare(b.label))
        },
        tagsValue(vm = this) {
            return vm.tagOptions.filter(item => vm.tags.includes(item.name))
        },
        tagsFilter(vm = this) {
            if (!vm.tags.length) return ''
            return ` AND (${vm.tags.map(tag => `tags:'${tag}'`).join(' OR ')})`
        },
        disabled(vm = this) {
            return !vm.folder || !vm.block || !vm.position
        },
        previewImg(vm = this) {
            return vm.file?.img || null
        },
        folderOptions(vm = this) {
            const type = vm.isRoutineType ? 'routines' : 'blocks'
            return [...vm.globalFolders, ...vm.folders].filter(folder => folder.type === type).map(folder => ({ ...folder, id: folder.id, label: vm.folderLabel(folder) })) || []
        },
        routineBlocks(vm = this) {
            return (vm.assignedRoutine?.blocks || []).filter(block => block?.exercises?.length)
        },
        positionOptions(vm = this) {
            const total = vm.assignedRoutine ? vm.routineBlocks.length : vm.blocksQuantity
            return Array.from({ length: (total || 0) + 1 }, (_, i) => i + 1)
        },
        previewBlocks(vm = this) {
            if (!vm.block) return []
            if (vm.isRoutineType) return vm.routineBlocksOf(vm.block).map(block => clearBlock(block))
            const block = clearBlock(vm.block.block || null)
            return block ? [block] : []
        },
        blockExercises(vm = this) {
            const exercises = []
            vm.previewBlocks.forEach(block => {
                block?.exercises?.forEach(exercise => {
                    if (exercise.type !== 'exercise') return
                    exercises.push({
                        event: {
                            ...exercise,
                            color: vm.blockColor,
                            isCircuit: block?.isCircuit || false,
                            sets: block?.sets || null,
                        }
                    })
                })
            })
            return exercises
        },
    },
    async mounted() {
        const vm = this
        if (vm.folders.length === 0) await vm.bindFolders(vm.coachId)
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapActions(['bindFolders', 'bindGlobalFolders', 'fetchExerciseCategories']),
        show(date) {
            const vm = this
            if (!vm.noSave && !vm.currentSelection) return vm.toast(vm.$t('common.need_select_student_or_group'), 'error')
            vm.loadRoutine(date)
            vm.dialog = true
            if (vm.positionOptions.length === 1) vm.position = 1
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.assignedRoutine = null
            if (vm.$refs.folders) vm.$refs.folders.model = null
            if (vm.$refs.blocks) vm.$refs.blocks.model = null
            if (vm.$refs.positions) vm.$refs.positions.model = null
            vm.block = null
            vm.folder = null
            vm.position = null
            vm.blockOptions = []
            vm.hiddenBlocks = 0
            vm.search = ''
            vm.tags = []
            vm.type = 'block'
            if (vm.$refs.tags) vm.$refs.tags.model = null
        },
        loadRoutine(date) {
            const vm = this;
            vm.date = date
            vm.assignedRoutine = vm.currentRoutines?.find(routine =>
                routine.rawDate?.full === date && (routine.routineNumber || 1) === vm.routineNumber
            ) || null
        },
        debouncedLoadBlockOptions: debounce(
            function () {
                this.loadBlockOptions()
            }, 1000),
        async loadBlockOptions() {
            const vm = this;
            if (!vm.folder) return
            vm.loadingSearch = true
            const filter = `coachId: ${vm.coachId} AND folder: ${vm.folder.id}${vm.tagsFilter}`
            const index = vm.isRoutineType ? RoutineIndex : BlockIndex
            const response = await index.search(vm.search, { filters: filter, hitsPerPage: 100 })
            vm.blockOptions = vm.isRoutineType
                ? response.hits.filter(hit => vm.routineBlocksOf(hit).length)
                : response.hits.filter(hit => hit.block?.exercises?.length)
            vm.hiddenBlocks = response.hits.length - vm.blockOptions.length
            vm.loadingSearch = false
        },
        routineBlocksOf(routine) {
            return (routine?.blocks || []).filter(block => block?.exercises?.length)
        },
        onChangeType(type) {
            const vm = this
            if (vm.type === type) return
            vm.type = type
            vm.folder = null
            vm.block = null
            vm.blockOptions = []
            vm.hiddenBlocks = 0
            vm.search = ''
            if (vm.$refs.folders) vm.$refs.folders.model = null
            if (vm.$refs.blocks) vm.$refs.blocks.model = null
        },
        toTagNames(selection) {
            return (selection || []).map(item => (item && typeof item === 'object') ? item.name : item)
        },
        blocksToInsert() {
            const vm = this
            if (vm.isRoutineType) return vm.routineBlocksOf(vm.block).map(block => clearBlock(block, false))
            return [clearBlock(vm.block.block, false)]
        },
        async addNewAssignedRoutine(blocks) {
            const vm = this

            const name = `${vm.currentSelection.name} - ${vm.$moment(vm.date).format('DD-MM-YYYY')}`
            let payloadAssignedRoutine = getPayloadBaseAssignedRoutine(name, vm.date, { coachId: vm.coachId }, blocks, vm.routineNumber)

            if (vm.collection === 'assignedRoutines') {
                payloadAssignedRoutine.studentId = vm.currentSelection.objectID
            } else {
                payloadAssignedRoutine.teamId = vm.currentSelection.teamId || null
                payloadAssignedRoutine.groupId = vm.currentSelection.id
            }

            const arRef = await db.collection(vm.collection).add(payloadAssignedRoutine)
            const assignedRoutineId = arRef.id

            if (vm.collection === 'groupAssignedRoutines') {
                let students = [...vm.currentSelection.students]
                if (vm.assignedRoutineToReplace.length > 0) {
                    let toDelete = vm.assignedRoutineToReplace.filter(ar => ar.replace).map(ar => {
                        return ar.assignedRoutineId
                    })
                    if (toDelete.length > 0) await deleteAssignedRoutinesGroup(toDelete)
                    let skippedStudents = vm.assignedRoutineToReplace.filter(ar => !ar.replace).map(ar => {
                        return ar.studentId
                    })
                    if (skippedStudents.length > 0) students = students.filter(st => !skippedStudents.includes(st))
                }
                await addAssignedRoutinesGroup({ id: assignedRoutineId, ...payloadAssignedRoutine, date: vm.date }, students)
            }

            vm.assignedRoutine = { id: assignedRoutineId, ...payloadAssignedRoutine }
        },
        async save() {
            const vm = this

            if (vm.noSave) {
                vm.$emit('success', vm.block)
                vm.hide()
                return
            }

            const toInsert = vm.blocksToInsert()
            if (!toInsert.length) return vm.toast(vm.$t('common.empty_blocks_hidden'), 'error')

            await vm.withLoading(async () => {
                if (!vm.assignedRoutine) {
                    await vm.addNewAssignedRoutine(toInsert)
                } else {
                    let blocks = [...vm.routineBlocks]
                    blocks.splice(vm.position - 1, 0, ...toInsert)
                    blocks = blocks.map((b, i) => ({ ...b, id: i + 1 }))
                    await db.collection(vm.collection).doc(vm.assignedRoutine.id).update({ blocks })
                    if (vm.collection === 'groupAssignedRoutines') await updateAssignedRoutinesGroup(vm.assignedRoutine.assignedRoutinesIds, blocks)
                }

                const usedCollection = vm.isRoutineType ? 'routines' : 'blocks'
                await db.collection(usedCollection).doc(vm.block.objectID).update({ used: FieldValue.increment(1) })
                vm.$emit('success', {
                    assignedRoutineId: vm.assignedRoutine.id,
                    routineId: vm.assignedRoutine.routineId || null,
                    date: vm.assignedRoutine.rawDate.full
                })
                vm.hide()
                vm.toast(vm.$t('common.saved_block'))
            })
        },
    },
    watch: {

        folder() {
            this.loadBlockOptions()
        },
        search() {
            this.debouncedLoadBlockOptions()
        },
        tags() {
            this.loadBlockOptions()
        }
    }
}
</script>

<style scoped>
.dlg-fields {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.ab-type {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.ab-type-label {
    font-size: var(--fs-label);
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.ab-block-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
}

.ab-hint {
    margin-top: -6px;
    font-size: var(--fs-label);
    color: var(--v-txtMuted-base);
}

.ab-preview-btn {
    margin-bottom: 2px;
    flex-shrink: 0;
}

.ab-preview-list {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.ab-preview-list >>> .exercise-item.is-hover-view {
    border-bottom: none;
}
</style>